import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';
import { useOptimizedCalculations } from '../store/optimizedGameStore';

interface CompanionCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompanionCollectionModal: React.FC<CompanionCollectionModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { companions, getMaxCompanionSlots, formatNumber } = useGameStore();
  const { setActiveCompanionOptimized, removeActiveCompanionOptimized } = useOptimizedCalculations();

  // Filter-Zustand für Begleiter-Sammlung
  const [filters, setFilters] = useState({
    rarity: 'all' as 'all' | 'common' | 'rare' | 'epic' | 'legendary' | 'godly' | 'mystic',
    ability: 'all' as 'all' | 'clickMultiplier' | 'coinPerSecond' | 'gemChance' | 'xpBonus' | 'gardenBonus' | 'autoClickPower' | 'upgradeDiscount' | 'prestigeBonus' | 'criticalHit' | 'healing' | 'shield' | 'counterAttack' | 'lifeSteal' | 'armorPenetration',
    status: 'all' as 'all' | 'active' | 'inactive',
    search: ''
  });

  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const getRarityColor = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-gray-400 to-gray-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityBorder = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'border-yellow-400';
      case 'epic': return 'border-purple-400';
      case 'rare': return 'border-blue-400';
      case 'common': return 'border-gray-400';
      default: return 'border-gray-400';
    }
  };

  const getRarityName = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      case 'common': return 'Gewöhnlich';
      default: return 'Unbekannt';
    }
  };

  const isCompanionActive = (companion: Companion) => {
    return companions.active.some(activeCompanion => activeCompanion?.id === companion.id);
  };

  const isGodlyLegendary = (companion: Companion) => {
    return companion.name.startsWith('★') && companion.name.endsWith('★');
  };

  const isMysticDivine = (companion: Companion) => {
    return companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
  };

  const getCompanionSlot = (companion: Companion) => {
    return companions.active.findIndex(activeCompanion => activeCompanion?.id === companion.id);
  };

  const handleCompanionClick = (companion: Companion) => {
    if (isCompanionActive(companion)) {
      // Entferne den Begleiter
      const slot = getCompanionSlot(companion);
      if (slot !== -1) {
        removeActiveCompanionOptimized(slot);
      }
    } else {
      // Füge den Begleiter hinzu
      setActiveCompanionOptimized(companion);
    }
  };

  const getAbilityIcon = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '👆';
      case 'coinPerSecond': return '💰';
      case 'gemChance': return '💎';
      case 'xpBonus': return '⭐';
      case 'gardenBonus': return '🌺';
      case 'autoClickPower': return '🤖';
      case 'upgradeDiscount': return '🏷️';
      case 'prestigeBonus': return '🌟';
      case 'criticalHit': return '💥';
      case 'healing': return '💚';
      case 'shield': return '🛡️';
      case 'counterAttack': return '🔄';
      case 'lifeSteal': return '🩸';
      case 'armorPenetration': return '🗡️';
      default: return '✨';
    }
  };

  const getAbilityLabel = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Multiplikator';
      case 'coinPerSecond': return 'Münzen pro Sekunde';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrungs-Bonus';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick-Kraft';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritische Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensentzug';
      case 'armorPenetration': return 'Rüstungsdurchdringung';
      default: return type;
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier':
      case 'gemChance':
      case 'xpBonus':
      case 'gardenBonus':
      case 'upgradeDiscount':
      case 'prestigeBonus':
      case 'lifeSteal':
        return '%';
      default:
        return '';
    }
  };

  // Filter-Funktionen
  const getFilteredCompanions = () => {
    return companions.discovered.filter(companion => {
      // Rarity Filter
      if (filters.rarity !== 'all') {
        if (filters.rarity === 'mystic' && !isMysticDivine(companion)) return false;
        if (filters.rarity === 'godly' && !isGodlyLegendary(companion)) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && companion.type !== filters.rarity) return false;
        if (filters.rarity !== 'mystic' && filters.rarity !== 'godly' && (isGodlyLegendary(companion) || isMysticDivine(companion))) return false;
      }

      // Ability Filter
      if (filters.ability !== 'all') {
        const hasAbility = companion.abilities.some(ability => ability.type === filters.ability);
        if (!hasAbility) return false;
      }

      // Status Filter
      if (filters.status !== 'all') {
        const isActive = isCompanionActive(companion);
        if (filters.status === 'active' && !isActive) return false;
        if (filters.status === 'inactive' && isActive) return false;
      }

      // Search Filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = companion.name.toLowerCase().includes(searchLower);
        const abilityMatch = companion.abilities.some(ability => 
          ability.description.toLowerCase().includes(searchLower) ||
          ability.type.toLowerCase().includes(searchLower)
        );
        if (!nameMatch && !abilityMatch) return false;
      }

      return true;
    });
  };

  const resetFilters = () => {
    setFilters({
      rarity: 'all',
      ability: 'all', 
      status: 'all',
      search: ''
    });
  };



  // Funktion zum Zusammenfassen der Fähigkeiten eines einzelnen Begleiters
  const getCombinedCompanionAbilities = (companion: Companion) => {
    const combinedAbilities = new Map<string, number>();

    companion.abilities.forEach(ability => {
      const key = ability.type;
      const currentValue = combinedAbilities.get(key) || 0;
      combinedAbilities.set(key, currentValue + ability.value);
    });

    return Array.from(combinedAbilities.entries()).map(([type, value]) => ({
      type,
      value,
      icon: getAbilityIcon(type),
      label: getAbilityLabel(type),
      suffix: getAbilitySuffix(type)
    }));
  };

  return (
    <AnimatePresence key="companion-collection-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 modal-container"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-y-auto modal-content scrollbar-hide"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6 rounded-t-xl z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="text-3xl sm:text-4xl">📚</div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">Begleiter-Sammlung</h1>
                    <div className="text-sm opacity-90">
                      {getFilteredCompanions().length}/{companions.discovered.length} Begleiter
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="text-white hover:text-gray-200 transition-colors duration-200 text-2xl sm:text-3xl font-bold min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 space-y-6 overflow-hidden">


              {/* Collapsible Filter-Menü */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200 shadow-lg overflow-hidden">
                {/* Header mit Toggle-Button */}
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">🔍</div>
                    <div>
                      <h3 className="text-xl font-bold text-purple-800">Filter & Suche</h3>
                      <p className="text-sm text-purple-600">
                        {(filters.rarity !== 'all' || filters.ability !== 'all' || filters.status !== 'all' || filters.search) 
                          ? 'Aktive Filter gesetzt' 
                          : 'Keine Filter aktiv'}
                      </p>
                    </div>
                  </div>
                  <div className={`text-2xl text-purple-600 transition-transform duration-300 ${isFilterExpanded ? 'rotate-180' : ''}`}>
                    ▼
                  </div>
                </div>

                {/* Collapsible Content */}
                <motion.div
                  initial={false}
                  animate={{ height: isFilterExpanded ? 'auto' : 0, opacity: isFilterExpanded ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="p-4 pt-0 space-y-6">
                    {/* Suchfeld - Prominent platziert */}
                    <div>
                      <div className="relative">
                        <input
                          type="text"
                          value={filters.search}
                          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                          placeholder="Begleiter-Name oder Fähigkeit suchen..."
                          className="w-full px-4 py-3 pl-12 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white/80 backdrop-blur-sm text-lg transition-all duration-200"
                        />
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-500 text-xl">
                          🔍
                        </div>
                      </div>
                    </div>

                    {/* Filter-Kategorien */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Seltenheit Filter - Mit Icons */}
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-purple-200">
                        <label className="block text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                          <span className="text-lg">⭐</span>
                          Seltenheit
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'all', label: 'Alle', icon: '🌟', color: 'bg-gray-100' },
                            { value: 'common', label: 'Gewöhnlich', icon: '⚪', color: 'bg-gray-300' },
                            { value: 'rare', label: 'Selten', icon: '🔵', color: 'bg-blue-300' },
                            { value: 'epic', label: 'Episch', icon: '🟣', color: 'bg-purple-300' },
                            { value: 'legendary', label: 'Legendär', icon: '🟡', color: 'bg-yellow-300' },
                            { value: 'godly', label: 'Göttlich', icon: '✨', color: 'bg-orange-300' },
                            { value: 'mystic', label: 'Mystisch', icon: '💫', color: 'bg-indigo-300' }
                          ].map(({ value, label, icon, color }) => (
                            <button
                              key={value}
                              onClick={() => setFilters(prev => ({ ...prev, rarity: value as any }))}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                                filters.rarity === value 
                                  ? 'bg-purple-500 text-white shadow-md' 
                                  : `${color} text-gray-700 hover:bg-purple-100`
                              }`}
                            >
                              <span className="text-lg">{icon}</span>
                              <span className="font-medium">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Fähigkeit Filter - Mit Icons */}
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-purple-200">
                        <label className="block text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                          <span className="text-lg">⚡</span>
                          Fähigkeit
                        </label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {[
                            { value: 'all', label: 'Alle Fähigkeiten', icon: '🌟' },
                            { value: 'clickMultiplier', label: 'Klick-Multiplikator', icon: '👆' },
                            { value: 'coinPerSecond', label: 'Münzen pro Sekunde', icon: '💰' },
                            { value: 'gemChance', label: 'Edelstein-Chance', icon: '💎' },
                            { value: 'xpBonus', label: 'Erfahrungs-Bonus', icon: '⭐' },
                            { value: 'gardenBonus', label: 'Garten-Bonus', icon: '🌺' },
                            { value: 'autoClickPower', label: 'Auto-Klick-Kraft', icon: '🤖' },
                            { value: 'upgradeDiscount', label: 'Upgrade-Rabatt', icon: '🏷️' },
                            { value: 'prestigeBonus', label: 'Prestige-Bonus', icon: '🌟' },
                            { value: 'criticalHit', label: 'Kritische Treffer', icon: '💥' },
                            { value: 'healing', label: 'Heilung', icon: '💚' },
                            { value: 'shield', label: 'Schild', icon: '🛡️' },
                            { value: 'counterAttack', label: 'Gegenangriff', icon: '🔄' },
                            { value: 'lifeSteal', label: 'Lebensentzug', icon: '🩸' },
                            { value: 'armorPenetration', label: 'Rüstungsdurchdringung', icon: '🗡️' }
                          ].map(({ value, label, icon }) => (
                            <button
                              key={value}
                              onClick={() => setFilters(prev => ({ ...prev, ability: value as any }))}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                                filters.ability === value 
                                  ? 'bg-purple-500 text-white shadow-md' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                              }`}
                            >
                              <span className="text-lg">{icon}</span>
                              <span className="font-medium text-sm">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Status Filter - Mit Icons */}
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-purple-200">
                        <label className="block text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                          <span className="text-lg">📊</span>
                          Status
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'all', label: 'Alle Begleiter', icon: '🌟', color: 'bg-gray-100' },
                            { value: 'active', label: 'Aktive Begleiter', icon: '✅', color: 'bg-green-100' },
                            { value: 'inactive', label: 'Inaktive Begleiter', icon: '⏸️', color: 'bg-gray-100' }
                          ].map(({ value, label, icon, color }) => (
                            <button
                              key={value}
                              onClick={() => setFilters(prev => ({ ...prev, status: value as any }))}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                                filters.status === value 
                                  ? 'bg-purple-500 text-white shadow-md' 
                                  : `${color} text-gray-700 hover:bg-purple-100`
                              }`}
                            >
                              <span className="text-lg">{icon}</span>
                              <span className="font-medium">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Aktive Filter anzeigen */}
                    {(filters.rarity !== 'all' || filters.ability !== 'all' || filters.status !== 'all' || filters.search) && (
                      <div className="p-4 bg-purple-100 rounded-xl border border-purple-300">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-purple-700 font-bold">🎯 Aktive Filter:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {filters.rarity !== 'all' && (
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              {filters.rarity === 'common' ? '⚪ Gewöhnlich' :
                               filters.rarity === 'rare' ? '🔵 Selten' :
                               filters.rarity === 'epic' ? '🟣 Episch' :
                               filters.rarity === 'legendary' ? '🟡 Legendär' :
                               filters.rarity === 'godly' ? '✨ Göttlich' : filters.rarity}
                            </span>
                          )}
                          {filters.ability !== 'all' && (
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              {getAbilityIcon(filters.ability)} {getAbilityLabel(filters.ability)}
                            </span>
                          )}
                          {filters.status !== 'all' && (
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              {filters.status === 'active' ? '✅ Aktiv' : '⏸️ Inaktiv'}
                            </span>
                          )}
                          {filters.search && (
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              🔍 "{filters.search}"
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Filter zurücksetzen */}
                    <div className="flex justify-center">
                      <button
                        onClick={resetFilters}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
                      >
                        <span className="text-lg">🔄</span>
                        <span>Alle Filter zurücksetzen</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Begleiter-Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-hidden p-2">
                {getFilteredCompanions().map((companion) => {
                  const isActive = isCompanionActive(companion);
                  const slotNumber = isActive ? getCompanionSlot(companion) + 1 : null;
                  
                  return (
                    <motion.div
                      key={companion.id}
                      className={`relative overflow-hidden rounded-2xl cursor-pointer min-h-[240px] max-w-full z-0 group
                        ${isActive ? 'ring-2 ring-green-400 ring-opacity-80 shadow-2xl transition-all duration-300 ease-out' : 'shadow-lg hover:shadow-xl'}
                        transition-all duration-300 ease-out`}
                      whileHover={{ 
                        scale: 1.02,
                        y: -2,
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleCompanionClick(companion)}
                      layout
                    >
                      {/* Hintergrund-Gradient */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${getRarityColor(companion.type)} opacity-90`} />
                      
                      {/* Glanz-Effekt */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      {/* Aktiv-Indikator */}
                      {isActive && (
                        <div className="absolute top-3 right-3 bg-green-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10">
                          Slot {slotNumber}
                        </div>
                      )}

                      {/* Göttlich-Indikator */}
                      {isGodlyLegendary(companion) && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10 animate-pulse">
                          ✨ GÖTTLICH ✨
                        </div>
                      )}

                      {/* Mystisch-Indikator */}
                      {isMysticDivine(companion) && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-indigo-400 to-purple-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg z-10 animate-pulse">
                          💫 MYSTISCH 💫
                        </div>
                      )}

                      {/* Hauptinhalt */}
                      <div className="relative z-10 p-4 h-full flex flex-col">
                        {/* Begleiter-Icon mit Glow-Effekt */}
                        <div className="text-center mb-3">
                          <div className={`text-4xl sm:text-5xl mb-1 drop-shadow-lg ${isActive ? 'animate-bounce' : 'group-hover:scale-105 transition-transform duration-300'}`}>
                            {companion.icon.startsWith('data:') ? (
                              <img 
                                src={companion.icon} 
                                alt={companion.name}
                                className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                                style={{ display: 'inline-block' }}
                              />
                            ) : (
                              companion.icon
                            )}
                          </div>
                        </div>
                        
                        {/* Begleiter-Name und Info */}
                        <div className="text-center mb-3 flex-1">
                          <div className="text-white font-bold text-base sm:text-lg break-words mb-1 drop-shadow-lg">
                            {companion.name}
                          </div>
                          <div className="text-white/90 text-xs font-medium mb-2">
                            {isMysticDivine(companion) ? 'Mystisch' : isGodlyLegendary(companion) ? 'Göttlich' : getRarityName(companion.type)}
                          </div>
                          
                          {/* Level-Anzeige mit modernem Design */}
                          <div className="flex justify-center items-center gap-2 mb-2">
                            <div className="bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 text-white/90 text-xs font-medium">
                              Level {companion.level || 1}
                            </div>
                            {companion.battleLevel && companion.battleLevel > 1 && (
                              <div className="bg-yellow-500/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-white text-xs font-bold">
                                ⚔️ Battle Lv.{companion.battleLevel}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Fähigkeiten in Raster - Zusammengefasst anzeigen */}
                        <div className="mb-3">
                          <div className="grid grid-cols-2 gap-1">
                            {getCombinedCompanionAbilities(companion).map((ability, idx) => {
                              return (
                                <div key={idx} className="bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1 text-xs break-words border border-white/20 hover:bg-white/25 transition-colors">
                                  <div className="text-center">
                                    <div className="text-lg mb-1">{ability.icon}</div>
                                    <div className="text-white/90 font-medium text-xs leading-tight mb-1 truncate">
                                      {ability.label}
                                    </div>
                                    <div className="text-white font-bold text-xs">
                                      +{formatNumber(ability.value)}{ability.suffix}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Footer mit Entdeckungsdatum */}
                        <div className="text-center">
                          <div className="text-white/60 text-xs mb-1">
                            Entdeckt: {new Date(companion.discoveredAt).toLocaleDateString('de-DE')}
                          </div>
                          
                          {/* Aktiv-Status */}
                          {isActive && (
                            <div className="bg-green-500/90 backdrop-blur-sm rounded-lg px-2 py-1 text-white text-xs font-bold">
                              ✓ Aktiv in Slot {slotNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Keine Ergebnisse */}
              {getFilteredCompanions().length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <div className="text-4xl mb-4">🔍</div>
                  <p className="text-lg mb-2">Keine Begleiter gefunden</p>
                  <p className="text-sm opacity-60">Versuche andere Filter oder setze sie zurück</p>
                </div>
              )}



              {/* Statistiken */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                <h3 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                  📊 Sammlungs-Statistiken
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {(['common', 'rare', 'epic', 'legendary'] as const).map((rarity) => {
                    const allOfType = companions.discovered.filter(c => c.type === rarity);
                    const normalOfType = allOfType.filter(c => !isGodlyLegendary(c));
                    
                    const count = rarity === 'legendary' ? normalOfType.length : allOfType.length;
                    const activeCount = companions.active.filter(c => c && c.type === rarity && !isGodlyLegendary(c)).length;
                    
                    return (
                      <div key={rarity} className={`p-3 rounded-lg bg-gradient-to-br ${getRarityColor(rarity)}`}>
                        <div className="text-white text-center">
                          <div className="text-lg font-bold">{count}</div>
                          <div className="text-xs opacity-80">{getRarityName(rarity)}</div>
                          {activeCount > 0 && (
                            <div className="text-xs bg-green-500/60 rounded px-1 mt-1">
                              {activeCount} aktiv
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Göttliche Begleiter separat */}
                  {companions.discovered.some(c => isGodlyLegendary(c)) && (
                    <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500">
                      <div className="text-white text-center">
                        <div className="text-lg font-bold">
                          {companions.discovered.filter(c => isGodlyLegendary(c)).length}
                        </div>
                        <div className="text-xs opacity-80">Göttlich</div>
                        <div className="text-xs bg-green-500/60 rounded px-1 mt-1">
                          {companions.active.filter(c => c && isGodlyLegendary(c)).length} aktiv
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mystische Begleiter separat */}
                  {companions.discovered.some(c => isMysticDivine(c)) && (
                    <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500">
                      <div className="text-white text-center">
                        <div className="text-lg font-bold">
                          {companions.discovered.filter(c => isMysticDivine(c)).length}
                        </div>
                        <div className="text-xs opacity-80">Mystisch</div>
                        <div className="text-xs bg-green-500/60 rounded px-1 mt-1">
                          {companions.active.filter(c => c && isMysticDivine(c)).length} aktiv
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompanionCollectionModal; 